import { cn } from "@design-system/utils";
import * as React from "react";
import type { ReactMaskOpts } from "react-imask";
import { useIMask } from "react-imask";

export interface InputProps extends React.ComponentProps<"input"> {
  debounce?: number;
  maskProps?: ReactMaskOpts;
}

function Input({
  className,
  type,
  maskProps,
  onChange,
  value,
  ...props
}: InputProps) {
  const maskOptions = React.useMemo(() => {
    return maskProps || {};
  }, [maskProps]);

  const { ref } = useIMask<HTMLInputElement>(
    maskOptions,
    maskProps
      ? {
          onAccept: (value, maskRef) => {
            const valueFormatted: string =
              maskProps?.unmask === "typed"
                ? (maskRef.typedValue as string)
                : maskProps.unmask
                  ? maskRef.unmaskedValue
                  : value;
            const syntheticEvent = {
              target: { value: valueFormatted },
              currentTarget: { value: valueFormatted },
            } as React.ChangeEvent<HTMLInputElement>;

            onChange?.(syntheticEvent);
          },
        }
      : {}
  );

  return (
    <input
      ref={maskProps ? ref : undefined}
      type={type}
      data-slot="input"
      onChange={maskProps ? undefined : onChange}
      value={maskProps ? undefined : value}
      className={cn(
        "text-shape-neutral-outlined-foreground-default file:text-shape-neutral-outlined-foreground-default placeholder:text-shape-neutral-outlined-foreground-muted bg-shape-neutral-outlined-background border-shape-neutral-outlined-border-emphasised flex h-9 w-full min-w-0 rounded-md border px-3 py-1 text-base shadow-xs transition-[color,box-shadow] outline-none file:inline-flex file:h-7 file:border-0 file:bg-transparent file:text-sm file:font-medium disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
        "focus-visible:ring-ring-default focus-visible:ring-[3px]",
        "aria-invalid:border-shape-critical-outlined-border-emphasised",
        className
      )}
      {...props}
    />
  );
}

export { Input };
